import React, { useCallback } from "react";
import { NavLink } from "react-router-dom";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileExport, faArrowLeft, faDrawPolygon, faRectangleList, faGears } from '@fortawesome/free-solid-svg-icons';
import ViewOutputModal from '../components/viewoutputmodal';
import { Accordion, Table, Button, ListGroup } from 'react-bootstrap';
import Joint, { DuctType } from "../models/joint";
import JointFile from "../models/joint-file";
import MachineSettings from "../models/machine-settings";

interface SidebarProps {
    file: JointFile | null;
    onSetFile(file: JointFile): void;

    selected: Joint | null;
    onSelect(joint: Joint): void;

    machineSettings: MachineSettings;
}

const mult = '\u00D7';

export default function Sidebar(props: SidebarProps) {
    const { file, onSetFile, selected, onSelect, machineSettings } = props;

    const [modalShow, setModalShow] = React.useState(false);

    const handleFileInput = useCallback(async (e: React.FormEvent<HTMLInputElement>) => {
        let file = e.currentTarget.files?.[0];
        if (file) {
            onSetFile(await JointFile.load(file));
        }
    }, [onSetFile])

    const ndsC1 = selected && machineSettings.notchDepthSettings.get(selected.connector1);
    const ndsC2 = selected && machineSettings.notchDepthSettings.get(selected.connector2);
    const lss = selected && machineSettings.lockSeamSettings.get(selected.lockSeam);

    return (
        <aside className="sidebar">
            <div>
                <nav className="header">
                    <NavLink
                        className="camoflauge d-block"
                        to="/">
                        <FontAwesomeIcon
                            icon={faArrowLeft}
                            className="mr-sm"
                        />
                        Back
                    </NavLink>
                </nav>
                <Accordion alwaysOpen flush defaultActiveKey={["0"]}>
                    <Accordion.Item eventKey="0">
                        <Accordion.Header>
                            <FontAwesomeIcon
                                icon={faDrawPolygon}
                                className="mr-sm"
                            />
                            Joint Selection
                        </Accordion.Header>
                        <Accordion.Body>
                            {file && <p>{file.fileName}</p>}
                            <p><input type="file" name="file" onInput={handleFileInput} /></p>
                            <ListGroup as="ul" className="joint-list">
                                {file?.joints.map((j, i) => (
                                    <ListGroup.Item as="li" key={i}
                                        active={j === selected}
                                        disabled={!j.enabled}
                                        onClick={() => onSelect(j)}
                                    >
                                        Qty {j.quantity}: {j.lengthOddLegs}{mult}{j.lengthEvenLegs} {j.ductTypeLabel}
                                        {j.reference && <><br />{j.reference}</>}
                                    </ListGroup.Item>
                                ))}
                            </ListGroup>
                        </Accordion.Body>
                    </Accordion.Item>
                </Accordion>

                <Accordion alwaysOpen flush defaultActiveKey={["1"]}>
                    {selected && (selected.ductType === DuctType.Sheets ? <SheetDetails selected={selected} /> : <JointDetails selected={selected} />)}
                    <Accordion.Item eventKey="2">
                        <Accordion.Header>
                            <FontAwesomeIcon
                                icon={faGears}
                                className="mr-sm"
                            />
                            Machine Settings
                        </Accordion.Header>
                        <Accordion.Body>
                            <Table>
                                <tbody>
                                    <tr>
                                        <th>Male Bend</th>
                                        <td>{machineSettings.maleBendTarget}&deg;</td>
                                    </tr>
                                    <tr>
                                        <th>Corner Bend</th>
                                        <td>{machineSettings.cornerBendTarget}&deg;</td>
                                    </tr>
                                </tbody>
                            </Table>
                            {selected &&
                                <Table style={{ width: '100%' }}>
                                    <tbody>
                                        <tr>
                                            <th>Connector 1</th>
                                            <th colSpan={2}>Notch Depth</th>
                                        </tr>
                                        <tr>
                                            <th>L&amp;T</th>
                                            <td colSpan={2}>{ndsC1?.ltDepth}</td>
                                        </tr>
                                        <tr>
                                            <th>Vee</th>
                                            <td colSpan={2}>{ndsC1?.veeDepth}</td>
                                        </tr>
                                    </tbody>
                                    <tbody>
                                        <tr>
                                            <th>Connector 2</th>
                                            <th colSpan={2}>Notch Depth</th>
                                        </tr>
                                        <tr>
                                            <th>L&amp;T</th>
                                            <td colSpan={2}>{ndsC2?.ltDepth}</td>
                                        </tr>
                                        <tr>
                                            <th>Vee</th>
                                            <td colSpan={2}>{ndsC2?.veeDepth}</td>
                                        </tr>
                                    </tbody>
                                    <tbody>
                                        <tr><th colSpan={3}>Lock Seams</th></tr>
                                        <tr>
                                            <th>Male Length</th>
                                            <th>Female Length</th>
                                            <th>Male Bend?</th>
                                        </tr>
                                        <tr>
                                            <td>{lss?.maleLength}</td>
                                            <td>{lss?.femaleLength}</td>
                                            <td>{lss?.bendMale ? 'Yes' : 'No'}</td>
                                        </tr>
                                    </tbody>
                                </Table>
                            }
                        </Accordion.Body>
                    </Accordion.Item>
                </Accordion>
            </div>
            <Button
                className="accordion-button accordion-modal"
                variant="link"
                disabled={!file}
                onClick={() => setModalShow(true)}>
                <FontAwesomeIcon
                    icon={faFileExport}
                    className="mr-sm"
                />
                Show File Output
            </Button>
            <ViewOutputModal
                raw={file?.raw}
                show={modalShow}
                onHide={() => setModalShow(false)}
            />
        </aside >
    );
}

export function JointDetails({ selected }: { selected: Joint }) {
    return (
        <Accordion.Item eventKey="1">
            <Accordion.Header>
                <FontAwesomeIcon
                    icon={faRectangleList}
                    className="mr-sm"
                />
                Joint Details
            </Accordion.Header>
            <Accordion.Body>
                <Table>
                    <tbody>
                        <tr>
                            <th>Quantity:</th>
                            <td>{selected.quantity}</td>
                        </tr>
                        <tr>
                            <th>Duct Type:</th>
                            <td>{selected.ductTypeLabel}</td>
                        </tr>
                    </tbody>
                    <tbody>
                        <tr>
                            <th>Leg Length</th>
                            <th>Odd</th>
                            <th>Even</th>
                        </tr>
                        <tr>
                            <td></td>
                            <td>{selected.lengthOddLegs}</td>
                            <td>{selected.lengthEvenLegs}</td>
                        </tr>
                    </tbody>
                    <tbody>
                        <tr>
                            <th>Connectors</th>
                            <th>C1</th>
                            <th>C2</th>
                        </tr>
                        <tr>
                            <td></td>
                            <td>{selected.c1TypeLabel}</td>
                            <td>{selected.c2TypeLabel}</td>
                        </tr>
                    </tbody>
                    <tbody>
                        <tr>
                            <th>Cleat Roller Type</th>
                            <td colSpan={2}>{selected.cleatLocationLabel}</td>
                        </tr>
                    </tbody>
                    <tbody>
                        <tr>
                            <th>Lock Seam:</th>
                            <th colSpan={2}>Sealant:</th>
                        </tr>
                        <tr>
                            <td>{selected.lockSeamTypeLabel}</td>
                            <td colSpan={2}>{selected.sealant ? "Yes" : "No"}</td>
                        </tr>
                    </tbody>
                    <tbody>
                        <tr>
                            <th>Insulation:</th>
                            <th colSpan={2}>Max Pin Spacing:</th>
                        </tr>
                        <tr>
                            <td>{selected.insulationThicknessLabel}</td>
                            <td colSpan={2}>{selected.pinSpacingLabel}</td>
                        </tr>
                    </tbody>
                    <tbody>
                        <tr>
                            <th>Tie Rods</th>
                            <th>Type</th>
                            <th>Row&nbsp;Count</th>
                        </tr>
                        <tr>
                            <th>Odd Legs</th>
                            <td>{selected.tieRodTypeOddLabel}</td>
                            <td>{selected.tieRodRowsOddLegs}</td>
                        </tr>
                        <tr>
                            <th>Even Legs</th>
                            <td>{selected.tieRodTypeEvenLabel}</td>
                            <td>{selected.tieRodRowsEvenLegs}</td>
                        </tr>
                        <tr>
                            <th>Hole Size</th>
                            <td colSpan={2}>{selected.tieRodHoldSizeLabel}</td>
                        </tr>
                    </tbody>
                    <tbody>
                        <tr>
                            <th>Coil Source</th>
                            <th>Gauge</th>
                            <th>Width</th>
                        </tr>
                        <tr>
                            <td>{selected.coilSourceLabel}</td>
                            <td>{selected.coilGauge}</td>
                            <td>{selected.coilWidth}"</td>
                        </tr>
                    </tbody>
                    <tbody>
                        <tr>
                            <th>Bead:</th>
                            <td colSpan={2}>{selected.beadTypeLabel}</td>
                        </tr>
                        <tr>
                            <th>Damper:</th>
                            <td colSpan={2}>{selected.damperLocationLabel}</td>
                        </tr>
                        <tr>
                            <th>Bend Corners:</th>
                            <td colSpan={2}>{selected.bendCorners ? "Yes" : "No"}</td>
                        </tr>
                        <tr>
                            <th>Reference</th>
                            <td colSpan={2}>{selected.reference}</td>
                        </tr>
                    </tbody>
                </Table>
            </Accordion.Body>
        </Accordion.Item>
    );
}

export function SheetDetails({ selected }: { selected: Joint }) {
    return (
        <Accordion.Item eventKey="1">
            <Accordion.Header>
                <FontAwesomeIcon
                    icon={faRectangleList}
                    className="mr-sm"
                />
                Sheet Details
            </Accordion.Header>
            <Accordion.Body>
                <Table>
                    <tbody>
                        <tr>
                            <th>Joint Qty:</th>
                            <td>{selected.quantity}</td>
                        </tr>
                        <tr>
                            <th>Duct Type:</th>
                            <td>{selected.ductTypeLabel}</td>
                        </tr>
                        <tr>
                            <th>Sheet Length</th>
                            <td>{selected.lengthOddLegs}</td>
                        </tr>
                    </tbody>
                    <tbody>
                        <tr>
                            <th>Coil Source</th>
                            <th>Gauge</th>
                            <th>Width</th>
                        </tr>
                        <tr>
                            <td>{selected.coilSourceLabel}</td>
                            <td>{selected.coilGauge}</td>
                            <td>{selected.coilWidth}"</td>
                        </tr>
                    </tbody>
                    <tbody>
                        <tr>
                            <th>Reference</th>
                            <td colSpan={2}>{selected.reference}</td>
                        </tr>
                    </tbody>
                </Table>
            </Accordion.Body>
        </Accordion.Item>
    );
}
